import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Menu, UserCircle } from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';
import { notificationUrl } from '../../utils/notificationNavigation';

export function TopBar({ title, subtitle, actions, onMenuOpen, onLogout, onOpenProfile, user, restaurantId, userRole, userName, onAdminNotifNavigate, onSwitchToEmployee }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const {notifications,unreadCount,error,loading,refresh,markRead,markAllRead}=useNotifications(user?.id);
  const navigate=useNavigate();

  return (
    <div className="app-topbar">
      {/* Mobile hamburger */}
      <button
        className="mobile-menu-btn"
        onClick={onMenuOpen}
        aria-label="Toggle navigation menu"
        style={{
          background: 'none',
          border: 'none',
          fontSize: '20px',
          cursor: 'pointer',
          color: 'var(--text)',
          padding: '10px 12px',
          borderRadius: 'var(--radius-sm)',
          flexShrink: 0,
        }}
      >
        <Menu size={20} />
      </button>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'var(--font-display)',
          fontSize: '16px',
          fontWeight: '700',
          color: 'var(--text)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {title}
        </div>
        {subtitle && (
          <div style={{
            fontSize: '12px',
            color: 'var(--text-3)',
            marginTop: '1px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {subtitle}
          </div>
        )}
      </div>

      {actions && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
          {actions}
        </div>
      )}

      {/* Employee View toggle — managers and admins with a restaurant */}
      {onSwitchToEmployee && (
        <button
          onClick={onSwitchToEmployee}
          aria-label="Switch to employee view"
          title="Employee View"
          style={{
            width: '44px', height: '44px',
            borderRadius: '50%',
            border: '1px solid var(--border)',
            background: 'var(--bg-secondary)',
            color: 'var(--text)',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <UserCircle size={18} />
        </button>
      )}

      {/* Notification Bell — managers and admins only */}
      {(restaurantId || userRole === 'admin') && (
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => { setShowDropdown(!showDropdown); refresh(); }}
            aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
            aria-haspopup="true"
            aria-expanded={showDropdown}
            style={{
              position: 'relative',
              width: '44px', height: '44px',
              borderRadius: '50%',
              border: '1px solid var(--border)',
              background: 'var(--bg-secondary)',
              color: 'var(--text)',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <div style={{
                position: 'absolute', top: '-2px', right: '-2px',
                background: '#dc2626', color: '#fff',
                borderRadius: '999px', minWidth: '16px', height: '16px',
                fontSize: '10px', fontWeight: '800',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0 4px',
              }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </div>
            )}
          </button>

          {showDropdown && (
            <>
              <div
                onClick={() => setShowDropdown(false)}
                style={{ position: 'fixed', inset: 0, zIndex: 998 }}
              />
              <div
                role="dialog"
                aria-label="Notifications"
                style={{
                  position: 'absolute', right: 0, top: '44px',
                  width: '320px', maxWidth: 'calc(100vw - 16px)', maxHeight: '400px',
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '16px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  zIndex: 999,
                  overflow: 'hidden',
                  display: 'flex', flexDirection: 'column',
                }}>
                <div style={{
                  padding: '14px 16px', borderBottom: '1px solid var(--border)',
                  fontWeight: '700', fontSize: '14px', color: 'var(--text)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  Notifications
                  {unreadCount > 0 && (
                    <button onClick={markAllRead} style={{ fontSize: '11px', color: 'var(--brand)', fontWeight: '700', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                      Mark all read
                    </button>
                  )}
                </div>
                <div style={{ overflowY: 'auto', flex: 1 }}>
                  {error ? <div role="alert" style={{padding:16}}>{error}<button onClick={refresh}>Try again</button></div> : loading ? <p style={{padding:16}}>Loading notifications…</p> : notifications.length === 0 ? (
                    <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-3)', fontSize: '13px' }}>
                      <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center' }}><Bell size={24} /></div>
                      No notifications
                    </div>
                  ) : notifications.slice(0,15).map(n => (
                    <div
                      key={n.id}
                      onClick={() => {
                        markRead(n.id);
                        setShowDropdown(false);
                        navigate(notificationUrl(n));
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => e.key === 'Enter' && e.currentTarget.click()}
                      style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid var(--border)',
                        cursor: 'pointer',
                        background: n.read ? 'transparent' : 'rgba(245,158,11,0.05)',
                        borderLeft: n.read ? 'none' : '3px solid #f59e0b',
                      }}
                      onMouseOver={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
                      onMouseOut={e => e.currentTarget.style.background = n.read ? 'transparent' : 'rgba(245,158,11,0.05)'}
                    >
                      <div style={{ fontWeight: n.read ? '400' : '700', fontSize: '13px', color: 'var(--text)' }}>
                        {n.title}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '2px' }}>
                        {n.body}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-4)', marginTop: '4px' }}>
                        {new Date(n.createdAt).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => { setShowDropdown(false); navigate('/notifications'); }}
                  style={{
                    width: '100%', padding: '11px 16px',
                    background: 'var(--bg-secondary)',
                    border: 'none', borderTop: '1px solid var(--border)',
                    color: 'var(--color-blue-500)', fontWeight: '700',
                    fontSize: '12px', cursor: 'pointer', textAlign: 'center',
                  }}
                >
                  View all notifications →
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {onOpenProfile && (
        <button
          onClick={onOpenProfile}
          aria-label="Open profile"
          title="Profile & Settings"
          style={{
            width: '34px', height: '34px', borderRadius: '50%',
            background: 'var(--brand)',
            color: 'var(--brand-contrast)', border: 'none', cursor: 'pointer',
            fontSize: '14px', fontWeight: '900', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {user?.name?.charAt(0).toUpperCase()}
        </button>
      )}
    </div>
  );
}
